// SSNInput component with XXX-XX-XXXX formatting

import { useState, useCallback } from 'react'

interface SSNInputProps {
  value: string
  onChange: (value: string) => void
  id?: string
  name?: string
  placeholder?: string
  required?: boolean
  className?: string
}

// Format SSN as XXX-XX-XXXX
function formatSSN(value: string): string {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '')

  // Limit to 9 digits
  const limited = digits.slice(0, 9)

  if (limited.length === 0) return ''
  if (limited.length <= 3) return limited
  if (limited.length <= 5) return `${limited.slice(0, 3)}-${limited.slice(3)}`
  return `${limited.slice(0, 3)}-${limited.slice(3, 5)}-${limited.slice(5)}`
}

// Extract raw digits from formatted value
function extractDigits(value: string): string {
  return value.replace(/\D/g, '').slice(0, 9)
}

export function SSNInput({
  value,
  onChange,
  id = 'ssn',
  name = 'ssn',
  placeholder = 'XXX-XX-XXXX',
  required = false,
  className = '',
}: SSNInputProps) {
  const [displayValue, setDisplayValue] = useState(() => formatSSN(value))

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value
    const digits = extractDigits(input)
    const formatted = formatSSN(digits)

    setDisplayValue(formatted)
    onChange(digits) // Return raw digits to parent
  }, [onChange])

  return (
    <input
      type="text"
      id={id}
      name={name}
      value={displayValue}
      onChange={handleChange}
      placeholder={placeholder}
      required={required}
      className={className}
      autoComplete="off"
      maxLength={11} // XXX-XX-XXXX
      inputMode="numeric"
    />
  )
}
